import { call, put, delay, all, takeLatest, select } from 'redux-saga/effects'
import { webApi, api } from 'lib/api'
import { errorWrap } from 'lib/saga-helpers'
import { captureException } from 'client/lib/sentry'

import { constants, resetLoginModalState, setField, setMode, setRegisterStep } from './actions'

import { MODAL_FROM_CONTEXTS, setUser, toggleModal, loadUser as loadUserAction } from 'store/ui/actions'
import { getNewToken } from 'store/ui/sagas'
import { setResetPasswordField, resetPasswordModalState, setBeginDescription } from 'store/reset-password-modal/actions'
import { fetchDropDownNotificationItems, fetchNotifications } from 'store/notifications/actions'

import * as analytics from 'lib/analytics'
import { SIGNUP_ACTIONS } from 'lib/analytics'
import { trackSignupClientError, trackSignupError, trackSignupSuccess } from 'lib/signup-analytics'
import { submitTrialForm } from 'lib/form-helpers'
import { userIsAtLeast } from 'lib/user'
import { PLANS } from '../../../server/lib/constants'
import { PASSWORD_ATTEMPTS_EXCEEDED_CODE, PASSWORD_ATTEMPTS_EXCEEDED_MESSAGE } from 'server/lib/auth-errors'

export const selectors = {
  organizationLicense: ({ ui }) => ui.organizationLicense,
  modalFromContext: ({ ui }) => ui.modalFromContext,
  validPassword: ({ loginModal }) => loginModal.password.value,
  checkoutIntent: ({ loginModal }) => loginModal.checkoutIntent,
  // SCI-3935: the signup-type step is shared by the register, login and enterprise
  // SAML forms, and the action only carries the email — so the live mode has to come
  // from the store or every login/SAML lookup would be reported as a registration.
  mode: ({ loginModal }) => loginModal.mode
}

// SCI-3935: marks a failure that happened after the account already existed — the
// trial hand-off or the post-auth navigation, not the auth call itself. Tagged onto
// the error so it travels with the attempt that produced it, rather than being held
// in module state the error handler has to trust.
const AFTER_ACCOUNT_CREATED = 'afterAccountCreated'

const tagAfterAccountCreated = e => {
  if (e && typeof e === 'object') {
    e[AFTER_ACCOUNT_CREATED] = true
  }
  return e
}

export function* submit({
  payload: {
    mode,
    fields: { name, email, password },
    history
  }
}) {
  const [modalFromContext, validPassword, organizationLicense, checkoutIntent] = yield all([
    select(selectors.modalFromContext),
    select(selectors.validPassword),
    select(selectors.organizationLicense),
    select(selectors.checkoutIntent)
  ])

  if (mode === 'register' && !validPassword) {
    // Returns before any request goes out, so neither signup_success nor
    // signup_error would otherwise fire — a user stuck here is indistinguishable
    // from one who simply walked away.
    trackSignupClientError(mode, 'password-mismatch')
    yield put({
      type: constants.SUBMIT_FAILURE,
      payload: {
        field: 'password',
        error: 'Passwords must match'
      }
    })
    return
  }

  const { localStorage } = window
  const sharedBy = localStorage?.getItem('sharedBy')
  const utm_medium = localStorage?.getItem('utm_medium')
  const utm_source = localStorage?.getItem('utm_source')
  const utm_campaign = localStorage?.getItem('utm_campaign')

  const endpoints = {
    register: '/users/register',
    login: '/users/login'
  }
  const payloads = {
    login: {
      email,
      password
    },
    register: {
      name,
      email,
      password,
      sharedBy,
      utm_medium,
      utm_source,
      utm_campaign
    }
  }
  // Pre-auth call: omit Authorization so a stale credential cannot block login (SCI-3965).
  const { data: user } = yield call(webApi.post, endpoints[mode], payloads[mode], { anonymous: true })

  // SCI-3935: matched success half of the funnel error-rate metric. Fired the
  // moment the register/login API call resolves, so it counts an actual API
  // outcome rather than a UI state.
  if (mode === 'register' || mode === 'login') {
    // The recorded checkout intent rides along so a Stripe hand-off that never reaches
    // the server is still detectable: this event with an intent, and no matching
    // server-side `trial_checkout_started` / `checkout_started`, is one that vanished.
    trackSignupSuccess(mode, 'submit', { checkoutIntent: checkoutIntent?.plan ?? null })
  }

  // Everything past here runs with the account already created. Those steps can
  // still fail — a broken trial hand-off, a navigation error — and are worth
  // reporting, but the attempt has already counted as a success. Tagging the error
  // lets the error-rate query drop the one attempt that would otherwise be counted
  // as both.
  try {
    yield call(completeAuthenticatedSubmit, {
      mode,
      user,
      history,
      modalFromContext,
      checkoutIntent,
      organizationLicense
    })
  } catch (e) {
    throw tagAfterAccountCreated(e)
  }
}

// The post-authentication half of `submit`: token refresh, store hydration, and the
// mode-specific hand-off to checkout or into the app. Split out so a failure here is
// distinguishable from a failure of the auth call itself.
function* completeAuthenticatedSubmit({ mode, user, history, modalFromContext, checkoutIntent, organizationLicense }) {
  yield getNewToken()
  yield put(setUser(user))
  yield put(loadUserAction())
  yield put(fetchNotifications({}))
  yield put(fetchDropDownNotificationItems())
  if (modalFromContext === MODAL_FROM_CONTEXTS.REFERENCE_CHECK) {
    yield put(toggleModal('referenceCheck'))
  }

  if (mode === 'register') {
    analytics.event({
      category: 'User',
      action: SIGNUP_ACTIONS.CREATED_ACCOUNT
    })

    yield put(toggleModal('login', false))

    if (window.location.pathname === '/mcp/authorize') {
      // Registering from the MCP OAuth authorize page: stay on the page so its
      // own premium-upgrade / consent flow runs, instead of navigating away.
    } else if (checkoutIntent && !userIsAtLeast(user, PLANS.premium) && !organizationLicense?.hasLicense) {
      // Only send the user to checkout when they explicitly started a trial/plan
      // (e.g. from /pricing, which records checkoutIntent). A plain sign-up with
      // no recorded intent just creates the account and keeps the user on their
      // current page — see SCI-3378.
      const redirectURL =
        window.location.origin + window.location.pathname + window.location.search + window.location.hash
      const rewardfulID = window?.Rewardful?.referral || ''
      const annual = checkoutIntent.annual ? 'true' : 'false'

      if (checkoutIntent.plan === PLANS.team) {
        // Team is always a paid checkout (never a trial) and carries the seat
        // count the user picked on /pricing — see I2.
        yield call(submitTrialForm, {
          action: CONFIG.stripe.checkoutSessionURL,
          fields: {
            createTrialCancelUrl: redirectURL,
            rewardfulID,
            plan: PLANS.team,
            annual,
            quantity: String(checkoutIntent.quantity || 2)
          }
        })
      } else {
        yield call(submitTrialForm, {
          action: CONFIG.stripe.trialCreationURL,
          fields: {
            createTrialCancelUrl: redirectURL,
            createTrialSuccessUrl: redirectURL,
            rewardfulID,
            plan: checkoutIntent.plan || PLANS.basic,
            annual
          }
        })
      }
    } else if (window.location.pathname === '/home' || window.location.pathname === '/') {
      history.push('/assistant')
    }
  } else if (mode === 'login') {
    yield put(toggleModal('login', false))
    analytics.event({
      category: 'User',
      action: 'Logged in'
    })
    if (
      checkoutIntent &&
      !userIsAtLeast(user, PLANS.premium) &&
      !organizationLicense?.hasLicense &&
      !user?.activatedTrial
    ) {
      const redirectURL =
        window.location.origin + window.location.pathname + window.location.search + window.location.hash
      const rewardfulID = window?.Rewardful?.referral || ''
      const annual = checkoutIntent?.annual ? 'true' : 'false'
      if (checkoutIntent?.plan === PLANS.team) {
        // Team is always a paid checkout (never a trial) and carries the seat
        // count the user picked on /pricing — see I2.
        yield call(submitTrialForm, {
          action: CONFIG.stripe.checkoutSessionURL,
          fields: {
            createTrialCancelUrl: redirectURL,
            rewardfulID,
            plan: PLANS.team,
            annual,
            quantity: String(checkoutIntent.quantity || 2)
          }
        })
      } else {
        yield call(submitTrialForm, {
          action: CONFIG.stripe.trialCreationURL,
          fields: {
            createTrialCancelUrl: redirectURL,
            createTrialSuccessUrl: redirectURL,
            rewardfulID,
            plan: checkoutIntent?.plan || PLANS.basic,
            annual
          }
        })
      }
    } else if (window.location.pathname === '/home' || window.location.pathname === '/') {
      history.push('/assistant')
    }
  } else if (mode === 'checkout') {
    yield put(toggleModal('login', false))
  }

  yield put({
    type: constants.SUBMIT_SUCCESS
  })

  yield delay(500)
  yield put({
    type: constants.RESET_LOGIN_MODAL_STATE
  })
}

function* resendVerificationEmail({ payload: { email } }) {
  yield call(webApi.get, `/users/resend-verification-email/${email}`)
  // The matched denominator for the resend_verification error below. Without it this
  // step could only be alerted on as an absolute count, where a rise in people asking
  // for a new link is indistinguishable from a rise in the send failing.
  trackSignupSuccess('email_verify', 'resend_verification')
  yield put(setMode('emailVerifyResent'))
}

export function* fetchSignupType({ payload: { email, mode } }) {
  // its called signupType in the database but we are using it here for determining signin type
  const { data } = yield call(api.get, `/users/signup-type?email=${encodeURIComponent(email)}`, { anonymous: true })
  // SCI-3935: step 0 of the register, login and SAML forms alike. If this call fails
  // the user is stranded here and never reaches /users/register or /users/login, so
  // the `submit`-step events can't see it. The mode comes from the action rather than
  // the store: it is the mode that *started* this lookup, which is what the event is
  // about, and re-reading the store here would mislabel a request the user toggled
  // away from while it was in flight.
  trackSignupSuccess(mode || (yield select(selectors.mode)), 'signup_type')
  yield put({
    type: constants.FETCH_SIGNUP_TYPE_SUCCESS,
    payload: {
      userSignupType: data?.signupType,
      userSignupTypeOrg: data?.organizationLicense,
      userSignupTypeEmailDomains: data?.emailDomains,
      // DB-sourced SAML for migrated orgs. When null, LoginForm falls
      // back to CONFIG.oAuth.saml for orgs still served from node-config.
      userSignupTypeSamlLoginUrl: data?.samlLoginUrl || null,
      userSignupTypeSamlSsoConfigName: data?.samlSsoConfigName || null,
      email
    }
  })
}

export default function* rootSaga() {
  yield takeLatest(
    constants.SUBMIT,
    errorWrap(
      submit,
      constants.SUBMIT_FAILURE,
      function* (
        e,
        {
          payload: {
            mode,
            fields,
            fields: { email, password }
          }
        }
      ) {
        const errorData = e?.response?.data
        const errorType = errorData?.type
        const errorField = errorData?.details?.[0]?.path?.[0]
        const errorMessage = errorData?.details?.[0]?.message
        const errorStatus = e?.response?.status

        // SCI-3935: log the failure to PostHog for funnel alerting before running
        // the UI-facing branching below. Two branches that reach here are NOT
        // funnel failures and are excluded: an SSO-enforced user is being
        // redirected to their IdP (a successful path), and "email verification
        // required" means the account was already created server-side (createUser
        // fired `User: Signed up` before returning the 401). Everything else — bad
        // password, duplicate email, validation, a 5xx, a network drop — is a real
        // failure; `trackSignupError` tags each so alerts can filter on `unexpected`.
        const isFunnelControlFlow =
          (errorData?.code === 'SSO_REQUIRED' && errorData?.samlLoginUrl) ||
          errorData?.code === 'EmailVerificationRequiredException'
        if ((mode === 'register' || mode === 'login') && !isFunnelControlFlow) {
          // `afterAccountCreated` marks a failure that happened once the account
          // already existed (a broken trial hand-off), so the error-rate query can
          // exclude it rather than counting one attempt as both success and error.
          trackSignupError(mode, e, e?.[AFTER_ACCOUNT_CREATED] ? { afterAccountCreated: true } : {})
        }

        if (
          mode === 'login' &&
          [
            'PasswordResetRequiredException', // from verify_password
            'InvalidPasswordException' // from migrate_user
          ].includes(errorData?.code)
        ) {
          yield put(toggleModal('login', false))
          yield put(toggleModal('resetPassword'))
          yield put(resetPasswordModalState())
          yield put(setResetPasswordField('email', email, null))
          yield put(setBeginDescription(errorData.message))
          yield put(resetLoginModalState())
        } else if (errorData?.code === PASSWORD_ATTEMPTS_EXCEEDED_CODE) {
          // Cognito has locked the account, so the correct password fails too —
          // "double-check your password" below would send the user into retries
          // that keep extending the lock.
          yield put(
            setField('password', password, {
              message: errorData?.message || PASSWORD_ATTEMPTS_EXCEEDED_MESSAGE
            })
          )
        } else if (errorData?.code === 'EmailVerificationRequiredException') {
          yield put(setMode('emailVerify'))
          yield put(
            setField('email', email, {
              message: 'An email verification link has been sent to you.'
            })
          )
        } else if (errorType === 'emailexists' || errorStatus === 409) {
          yield put(
            setField('password', password, {
              message: 'User with that email already exists, please try to login.'
            })
          )
        } else if (errorType === 'invalid_email') {
          yield put(setRegisterStep(0))
          yield put(
            setField('email', email, {
              message: 'Please use a valid email address. Temporary or disposable email addresses are not allowed.'
            })
          )
        } else if (errorType === 'request_blocked') {
          yield put(setRegisterStep(0))
          yield put(
            setField('email', email, {
              message:
                errorData?.message ||
                'Your request was blocked by our security provider. Please try a different network or contact us for help.'
            })
          )
        } else if (errorType === 'forbidden') {
          yield put(setRegisterStep(0))
          yield put(
            setField('email', email, {
              message: errorData?.message || 'You do not have permission to perform this action.'
            })
          )
        } else if (errorData?.code === 'SSO_REQUIRED' && errorData?.samlLoginUrl) {
          // SSO is enforced for this user's org — redirect them to their IdP
          yield put(toggleModal('login', false))
          yield put(resetLoginModalState())
          window.location.href = errorData.samlLoginUrl
        } else if ([401, 403, 404].includes(errorStatus)) {
          yield put(
            setField('password', password, {
              message: "We couldn't log you in.\nDouble-check your email and password."
            })
          )
        } else if (errorStatus === 500) {
          yield put(setRegisterStep(0))
          yield put(
            setField('email', email, {
              message: `Failed to ${mode} please try again.`
            })
          )

          captureException(e, { extra: { context: 'Could not login', errorStatus } })
        } else if (errorType === 'user_schema_validation' && errorField) {
          if (['name', 'email'].includes(errorField)) {
            yield put(setRegisterStep(0))
          }
          yield put(
            setField(errorField, fields[errorField], {
              message: errorMessage.replace(/"/g, '')
            })
          )
        } else {
          yield put(
            setField('email', email, {
              message: 'Something went wrong! Please try again.'
            })
          )
          captureException(e, { extra: { context: 'Could not login' } })
        }
      }
    )
  )
  yield takeLatest(
    constants.FETCH_SIGNUP_TYPE,
    errorWrap(fetchSignupType, constants.FETCH_SIGNUP_TYPE_FAILURE, function* (e, { payload: { mode } }) {
      const signupMode = mode || (yield select(selectors.mode))
      const isUnknownSignupCandidate =
        ['register', 'login'].includes(signupMode) &&
        e?.response?.status === 404 &&
        e?.response?.data?.detail === 'No user or license not found'

      // An unknown email is the expected result for a new signup. Keep dispatching
      // FETCH_SIGNUP_TYPE_FAILURE because the reducer uses it to advance registration
      // or keep an unknown login on the email step, but count the completed lookup as
      // a success rather than an outage in the signup funnel.
      if (isUnknownSignupCandidate) {
        trackSignupSuccess(signupMode, 'signup_type')
        return
      }

      // Same reasoning as the success path: report the mode that started the lookup.
      trackSignupError(signupMode, e, {}, 'signup_type')
    })
  )

  yield takeLatest(
    constants.RESEND_VERIFICATION_EMAIL,
    errorWrap(resendVerificationEmail, constants.FETCH_SIGNUP_TYPE_FAILURE, e => {
      // The account exists but is unusable until verified. If resending the link
      // fails there is no way forward for the user, so it's a funnel failure even
      // though registration itself succeeded.
      //
      // Reported under its own mode and step rather than as a register submit
      // failure: the `emailVerify` screen is reached from a login attempt as well as
      // a register one, so 'register' would mislabel half of these — and with no
      // matching submit-step success, they would inflate that step's error rate.
      trackSignupError('email_verify', e, { reason: 'resend-verification-failed' }, 'resend_verification')
    })
  )
}
